import {
    AppIdentifiers,
    IAppClosedTopicPayload,
    IDivHostedAppInitParameters,
    IOpenAppData,
    IAppLaunchInfo
} from '@sparkware/uc-sdk-core';
import {ServiceBase} from '../../ServiceBase';
import {IElasticEventData, IStructuredLogger} from '../../../../common/interfaces/structured-logging-service-interface';
import {IServiceFactoryExtended} from '../../service-factory-extended.interface';
import {getCasinofiedDeviceInfo} from "../casinofied-helper-functions";
import {Check} from "@sparkware/app-utils";
import {
    IActionHandlerEventParams,
    IAnalyticsDisplayMessage, ICasinofiedAnalyticsService,
    IMessageActionTakenEventParams
} from "../../../../common/interfaces/casinofied-analytics-proxy.interface";

export interface IAppInitResultReportingDetails {
    errorCode?: number;
    errorDescription?: string;
    loadingTime?: number;
}

export class CasinofiedAnalyticsService extends ServiceBase implements ICasinofiedAnalyticsService {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        services.sdk.events.onBeforeOAuthStateChanged(() => {
            if (services.user.isAuthorized) { //user is about to logout
                this._sessionClosed();
            }

        });

        this.reaction(() => services.user.isAuthorized, (isAuthorized) => {
            this._sequentialNumber = 0;
            if (isAuthorized) {
                this._authenticationResult();
            }
        });

        this.services.window.subscribeTo.focusLost(() => this._userMinimizedClient());
        this.services.window.subscribeTo.focusGained(() => this._userResumedClient());
        this.services.window.subscribeTo.unload(() => this._userClosedClient());
        this.services.sdk.events.onLoginSessionResumed(() => this._loginSessionResumed());
    }

    private get _sequentialNumber(): number {
        const value = this.services.localStorage.getItem('analytics_sequential_number');
        if (Check.isNullOrUndefined(value)) {
            return 0;
        }
        return parseInt(value);

    }

    private set _sequentialNumber(value: number) {
        this.services.localStorage.setItem('analytics_sequential_number', value);
    }



    private _createLogger(eventName: string, eventData?: IElasticEventData): IStructuredLogger {
        return this.services.analytics.getLogger(eventData)
            .setEventType(eventName, 'Log', 'Analytics')
            .setAppID(AppIdentifiers.CasinoClient)
            .setSequentialNumber(this._sequentialNumber);
    }

    private _createMessageId(): string {
        this._sequentialNumber++;
        const cid = this.services.user.cid;
        const playerSessionId = this.services.user.userData.sessionId;
        return `${cid}_${playerSessionId}_${AppIdentifiers.CasinoClient}_${this._sequentialNumber}`;
    }

    openAppRequest(openAppData: IOpenAppData): void {
        try {
            const eventName = 'uc_open_app_request';

            const logger = this._createLogger(eventName, {
                [eventName]: {
                    messageID: this._createMessageId(),
                    targetAppID: openAppData.appID,
                    subBrandID: this.services.sdk.getSubBrandID(),
                    sequentialCorrelationID: this._extractSequentialCorrelationID(openAppData.launchInfo),
                    launchInfo: {
                        channel: openAppData.launchInfo?.channel,
                        sourceAppID: openAppData.launchInfo?.sourceAppID,
                        sourceAppVersion: openAppData.launchInfo?.sourceAppVersion,
                        trigger: openAppData.launchInfo?.trigger
                    }
                }
            });
            logger.setCorrelationId(openAppData.launchInfo?.businessCorrelationID)
                .setBusinessCorrelationID(openAppData.launchInfo?.businessCorrelationID)
                .info();
        } catch (err) {
            this.services.logger.error(`CasinofiedAnalyticsService.openAppRequest failed`, err);
        }

    }

    appInitRequest(appInitParams: IDivHostedAppInitParameters): void {
        try {
            const eventName = 'uc_app_init_request';
            const logger = this._createLogger(eventName, {
                [eventName]: {
                    messageID: this._createMessageId(),
                    targetAppID: appInitParams.applicationContext.appID,
                    subBrandID: appInitParams.applicationContext.subBrandID,
                    currencyCode: appInitParams.applicationContext.currencyCode,
                    language: appInitParams.applicationContext.languageCode,
                    sequentialCorrelationID: this._extractSequentialCorrelationID(appInitParams.applicationContext.launchInfo),
                    productPackageFromID: appInitParams.applicationContext.productPackageFromID,
                    productPackageToID: appInitParams.applicationContext.productPackageToID,
                    launchInfo: {
                        channel: appInitParams.applicationContext.launchInfo.channel,
                        sourceAppID: appInitParams.applicationContext.launchInfo.sourceAppID,
                        sourceAppVersion: appInitParams.applicationContext.launchInfo.sourceAppVersion,
                        trigger: appInitParams.applicationContext.launchInfo.trigger,
                        appSpecificParameters: appInitParams.applicationContext.launchInfo.appSpecificParameters,
                        openingMode: appInitParams.applicationContext.launchInfo.openingMode,
                    },
                    userInfo: {
                        userMode: appInitParams.userInfo.userMode,
                        regulationTypeID: appInitParams.userInfo.regulationTypeID,
                        isVip: appInitParams.userInfo.isVIP,
                        isFTD: appInitParams.userInfo.isFTD,
                        isPromotionPermitted: appInitParams.userInfo.isPromotionPermitted,
                        RMS: appInitParams.userInfo.RMS,
                    },
                    timeZoneUTCOffset: appInitParams.geoInfo.timeZoneUTCOffset,
                    deviceInfo: {
                        deviceTypeID: appInitParams.deviceInfo.deviceType,
                        os: appInitParams.deviceInfo.OS,
                        isHybrid: appInitParams.deviceInfo.isHybrid,
                        isNative: appInitParams.deviceInfo.isNative,
                        browserType: appInitParams.deviceInfo.browserType,
                        resolution: this._getResolution()
                        //proxyType
                    }
                }
            });

            logger.setCorrelationId(appInitParams.applicationContext.launchInfo.businessCorrelationID)
                .setBusinessCorrelationID(appInitParams.applicationContext.launchInfo.businessCorrelationID)
                .info();
        } catch (err) {
            this.services.logger.error(`CasinofiedAnalyticsService.appInitRequest failed`, err);
        }
    }

    appInitResult(appInitParams: IDivHostedAppInitParameters, appInitResultReportingDetails?: IAppInitResultReportingDetails) {
        appInitResultReportingDetails = {
            errorCode: 0,
            errorDescription: '',
            ...appInitResultReportingDetails
        }
        const getLaunchInfo = () => {
            try {
                return {
                    channel: appInitParams.applicationContext.launchInfo.channel,
                    sourceAppID: appInitParams.applicationContext.launchInfo.sourceAppID,
                    sourceAppVersion: appInitParams.applicationContext.launchInfo.sourceAppVersion,
                    trigger: appInitParams.applicationContext.launchInfo.trigger,
                    appSpecificParameters: appInitParams.applicationContext.launchInfo.appSpecificParameters,
                    openingMode: appInitParams.applicationContext.launchInfo.openingMode,
                };
            } catch (err) {
                return null;
            }
        }

        try {
            const eventName = 'uc_app_init_result';
            const logger = this._createLogger(eventName, {
                [eventName]: Object.assign({
                    messageID: this._createMessageId(),
                    subBrandID: appInitParams.applicationContext.subBrandID,
                    sequentialCorrelationID: this._extractSequentialCorrelationID(appInitParams.applicationContext.launchInfo),
                    isOk: appInitResultReportingDetails.errorCode === 0,
                    errorCode: appInitResultReportingDetails.errorCode,
                    errorDescription: appInitResultReportingDetails.errorDescription,
                    targetAppID: appInitParams.applicationContext.appID,
                    launchInfo: getLaunchInfo()
                }, Check.isNullOrUndefined(appInitResultReportingDetails.loadingTime) ? {} : {loadingTime: appInitResultReportingDetails.loadingTime})//add duration if it exists
            });

            logger.setCorrelationId(appInitParams.applicationContext.launchInfo?.businessCorrelationID)
                .setBusinessCorrelationID(appInitParams.applicationContext.launchInfo?.businessCorrelationID)
                .info();
        } catch (err) {
            this.services.logger.error(`CasinofiedAnalyticsService.appInitResult failed`, err);
        }

    }

    private _extractSequentialCorrelationID(launchInfo: IAppLaunchInfo): number {
        if (!launchInfo?.businessCorrelationID) {
            return 1;
        } else {
            return this.services.appLauncher.getSequentialCorrelationIdHandler(launchInfo.businessCorrelationID)?.getCurrentValue() ?? 1
        }
    }

    appClosed(appClosedParams: IAppClosedTopicPayload) {
        try {


            const eventName = 'uc_app_closed';
            const logger = this._createLogger(eventName, {
                [eventName]: {
                    messageID: this._createMessageId(),
                    subBrandID: this.services.sdk.getSubBrandID(),
                    sequentialCorrelationID: 1,
                    targetAppID: appClosedParams.appID,
                    appCloseReason: appClosedParams.closeReason, //TODO - what values should be here for closeReason
                    channel: appClosedParams.launchInfo?.channel
                }
            });

            logger.setCorrelationId(appClosedParams.launchInfo?.businessCorrelationID)
                .setBusinessCorrelationID(appClosedParams.launchInfo?.businessCorrelationID)
                .info();
        } catch (err) {
            this.services.logger.error(`CasinofiedAnalyticsService.appClosed failed`, err);
        }
    }

    messageDisplay(params: IAnalyticsDisplayMessage): void {
        try {
            const eventName = 'uc_app_message_displayed';
            const extraDataEventName = 'extraData';

            const eventData = {
                [eventName]: {
                    targetAppID: AppIdentifiers.CasinoClient,
                    messageID: this._createMessageId(),
                    subBrandID: this.services.sdk.getSubBrandID(),
                    displayMessageID: params.displayMessageID,
                    sequentialCorrelationID: 1,
                    displayMessageName: params.displayMessageName
                }
            }

            if(params.extraData){
                eventData[extraDataEventName] = {
                    totalStakes: params.extraData.totalStakes,
                    netPosition: params.extraData.netPosition,
                    totalWins: params.extraData.totalWins,
                    totalLosses: params.extraData.totalLosses,
                    bankrollCurr: params.extraData.bankrollCurr
                }
            }

            const logger = this._createLogger(eventName, eventData);

        logger.setCorrelationId(params.correlationID || params.businessCorrelationID)
            .setBusinessCorrelationID(params.businessCorrelationID)
            .info();
    } catch(err) {
        this.services.logger.error(`CasinofiedAnalyticsService.messageDisplay failed`, err);
    }
}

    private _getResolution() {
    return window.screen.width * window.devicePixelRatio + "x" + window.screen.height * window.devicePixelRatio;
}

    private _authenticationResult(): void {
    try {
        const eventName = 'uc_authentication_result';
        const deviceInfo = getCasinofiedDeviceInfo(this.services);
        const logger = this._createLogger(eventName, {
            [eventName]: {
                isOk: true,
                errorCode: 0,
                errorDescription: "",
                messageID: this._createMessageId(),
                subBrandID: this.services.sdk.getSubBrandID(),
                deviceInfo: {
                    deviceTypeID: deviceInfo.deviceType,
                    os: deviceInfo.OS,
                    isHybrid: deviceInfo.isHybrid,
                    isNative: deviceInfo.isNative,
                    browserType: deviceInfo.browserType,
                    resolution: this._getResolution()
                }
            }
        });

        logger.info();

    } catch(err) {
        this.services.logger.error(`CasinofiedAnalyticsService._authenticationResult failed`, err);
    }
}

    private _sessionClosed(): void {
    try {
        const eventName = 'uc_session_closed';
        const logger = this._createLogger(eventName, {
            [eventName]: {
                messageID: this._createMessageId(),
                subBrandID: this.services.sdk.getSubBrandID(),
                closingReasonID: 1
            }
        });

        logger.info();

    } catch(err) {
        this.services.logger.error(`CasinofiedAnalyticsService._sessionClosed failed`, err);
    }
}

actionHandler(params: IActionHandlerEventParams): void {
    try {
        const eventName = 'uc_action_handler_result';

        const logger = this._createLogger(eventName, {
            [eventName]: {
                messageID: this._createMessageId(),
                subBrandID: this.services.sdk.getSubBrandID(),
                sequentialCorrelationID: 1,
                actionName: params.actionName,
                actionHandlerStep: params.actionHandlerStep,
                actionResult: params.actionResult
            }
        });

        logger.setCorrelationId(params.businessCorrelationID)
            .setBusinessCorrelationID(params.businessCorrelationID)
            .info();
    } catch(err) {
        this.services.logger.error(`CasinofiedAnalyticsService.actionHandler failed`, err);
    }

}

messageActionTaken(params: IMessageActionTakenEventParams) {
    try {
        const eventName = 'uc_app_message_action_taken';

        const logger = this._createLogger(eventName, {
            [eventName]: {
                messageID: this._createMessageId(),
                subBrandID: this.services.sdk.getSubBrandID(),
                actionType: params.actionType,
                displayMessageName: params.displayMessageName
            }
        });

        if (params.sendLogLevelOnly) {
            logger.setEventType(eventName, 'Log');
        }

        logger.setCorrelationId(params.correlationID || params.businessCorrelationID)
            .setBusinessCorrelationID(params.businessCorrelationID)
            .info();

    } catch (err) {
        this.services.logger.error(`CasinofiedAnalyticsService.messageActionTaken failed`, err);
    }
}

reportDuration(loadSubject: string, duration: number, appId: string): void {
    try {
        const eventName = 'uc_app_duration_report';
        const logger = this._createLogger(eventName, {
            [eventName]: {
                messageID: this._createMessageId(),
                subBrandID: this.services.sdk.getSubBrandID(),
                loadSubject: loadSubject,
                loadingTime: duration,
                targetAppID: appId
            }
        });
        logger.info();

    } catch(err) {
        this.services.logger.error(`CasinofiedAnalyticsService.reportDuration failed`, err);
    }
}

    private _userClosedClient() {
    try {
        if (!this.services.user.isAuthorized) {
            return;
        }
        const eventName = 'clientStateChanged';

        const logger = this._createLogger(eventName, {
            [eventName]: {
                subBrandID: this.services.sdk.getSubBrandID(),
                messageID: this._createMessageId(),
                clientStateID: 3,
                clientStateName: "Closed"
            }
        });

        logger.info();

    } catch (err) {
        this.services.logger.error(`CasinofiedAnalyticsService.userClosedClient failed`, err);
    }
}

    private _userMinimizedClient() {
    try {
        if (!this.services.user.isAuthorized) {
            return;
        }
        const eventName = 'clientStateChanged';

        const logger = this._createLogger(eventName, {
            [eventName]: {
                subBrandID: this.services.sdk.getSubBrandID(),
                messageID: this._createMessageId(),
                clientStateID: 2,
                clientStateName: "Background"
            }
        });

        logger.info();

    } catch (err) {
        this.services.logger.error(`CasinofiedAnalyticsService.userMinimizedClient failed`, err);
    }
}

    private _userResumedClient() {
    try {
        if (!this.services.user.isAuthorized) {
            return;
        }
        const eventName = 'clientStateChanged';

        const logger = this._createLogger(eventName, {
            [eventName]: {
                subBrandID: this.services.sdk.getSubBrandID(),
                messageID: this._createMessageId(),
                clientStateID: 1,
                clientStateName: "Foreground"
            }
        });

        logger.info();

    } catch (err) {
        this.services.logger.error(`CasinofiedAnalyticsService.userResumedClient failed`, err);
    }
}
    private _loginSessionResumed() {
    try {
        if (!this.services.user.isAuthorized) {
            return;
        }
        const eventName = 'clientStateChanged';

        const logger = this._createLogger(eventName, {
            [eventName]: {
                subBrandID: this.services.sdk.getSubBrandID(),
                messageID: this._createMessageId(),
                clientStateID: 4,
                clientStateName: "SessionResumed"
            }
        });

        logger.info();

    } catch (err) {
        this.services.logger.error(`CasinofiedAnalyticsService._loginSessionResumed failed`, err);
    }
}
}
